import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import Auth from '../../../../data/auth-service';
import FilterService from '../../../../data/filter-service';
import DataContext from '../../../../data/data-context';
import SystemService from '../../../../data/system-service';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
    },
    button: {
        marginRight: theme.spacing(1),
    },
}));

const CreateFilter = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [hasChanges, setHasChanges] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const { systemSettings } = useContext(DataContext);
    const routeParams = useParams();
    const [filterService, setFilterService] = useState();
    const [filterdata, setFilterData] = useState({
        Description: '',
        dns: false,
    });
    const awsPerms = Auth.permissions.get('AWS');
    const [readonly] = useState(!awsPerms.write);

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            if (!readonly) {
                SystemService.awsCredAccess().then((res) => {
                    if (res) {
                        const region = systemSettings.aws.as_conf.region;
                        const accessKeyId = systemSettings.aws.user_creds.access_key_id;
                        const secretAccessKey = res.AWSSK;
                        initializeService(region, accessKeyId, secretAccessKey);
                    }
                });
            }
        }
    }, [systemSettings]);

    const initializeService = async (awsregion, accessKeyId, secretAccessKey) => {
        const config = {
            region: awsregion,
            credentials: {
                accessKeyId,
                secretAccessKey,
            },
        };

        const filterservice = new FilterService(config);
        setFilterService(filterservice);
    };

    const getFilterId = async (TrafficMirrorFilterId) => {
        try {
            if (!filterService || typeof filterService.getFilterById !== 'function') {
                throw new Error(
                    'Filter service is not properly initialized or getFilterById method is not available'
                );
            }
            const filter = [TrafficMirrorFilterId];
            const res = await filterService.getFilterById(filter);

            if (res) {
                return res;
            } else {
                throw new Error('Filter not found');
            }
        } catch (error) {
            console.error('Error fetching Filter by Id:', error);
            toast.error(`Failed to fetch Filter by Id: ${error.message}`);
            return null;
        }
    };

    useEffect(() => {
        const fetchFilterId = async () => {
            if (routeParams.TrafficMirrorFilterId && filterService) {
                try {
                    const res = await getFilterId(routeParams.TrafficMirrorFilterId);
                    if (res) {
                        setIsUpdate(true);
                        const dnsValue =
                            // eslint-disable-next-line no-unneeded-ternary
                            res[0] &&
                                Array.isArray(res[0]?.NetworkServices) &&
                                res[0].NetworkServices.includes('amazon-dns')
                                ? true
                                : false;
                        const description = res[0].Description;

                        setFilterData({
                            Description: description,
                            dns: dnsValue,
                        });
                    }
                } catch (error) {
                    console.error('Error fetching Filter:', error);
                }
            }
        };

        fetchFilterId();
    }, [filterService]);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFilterData((prevData) => ({
            ...prevData,
            [name]: name === 'dns' ? checked : value,
        }));
        setHasChanges(true);
    };

    const handleFilterClose = () => {
        navigate('/system/port_mirroring/aws', { state: { tab: 1 } });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isUpdate) {
            try {
                const res = await filterService.modifyFilter(
                    filterdata,
                    routeParams.TrafficMirrorFilterId
                );
                if (res.$metadata.httpStatusCode === 200) {
                    toast.success('Filter updated successfully');
                    setIsUpdate(false);
                    setHasChanges(false);
                    SystemService.addAuditRecord({ "source": "AWS config", "subject": filterdata.Description, "action": "update mirror filter", "result": true });
                    handleFilterClose();
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                console.error('Error:', error);
                SystemService.addAuditRecord({ "source": "AWS config", "subject": filterdata.Description, "action": "Error: " + error, "result": false });
                toast.error(`Failed to update Filter.Please try again. \n${error}`);
            }
        } else {
            try {
                const res = await filterService.createFilter(filterdata);
                if (res.$metadata.httpStatusCode === 200) {
                    if (filterdata.dns) {
                        const dnsRes = await filterService.modifyFilter(
                            filterdata,
                            res.TrafficMirrorFilter.TrafficMirrorFilterId
                        );
                        if (dnsRes.$metadata.httpStatusCode === 200) {
                            setHasChanges(false);
                            handleFilterClose();
                            SystemService.addAuditRecord({ "source": "AWS config", "subject": filterdata.Description, "action": "create mirror filter", "result": true });
                            toast.success('Filter created successfully');
                        } else {
                            throw new Error(`Failed to add dns. HTTP status code: ${dnsRes.$metadata.httpStatusCode}`);
                        }
                    } else {
                        setHasChanges(false);
                        handleFilterClose();
                        console.log(res);
                        SystemService.addAuditRecord({ "source": "AWS config", "subject": filterdata.Description, "action": "create mirror filter", "result": true });
                        toast.success('Filter created successfully');
                    }
                } else {
                    throw new Error(`Failed to create filter. HTTP status code: ${res.$metadata.httpStatusCode}`);
                }
            } catch (error) {
                console.error('Error:', error);
                SystemService.addAuditRecord({ "source": "AWS config", "subject": "AWS Mirror Filter", "action": "Error: " + error, "result": false });
                toast.error(`An error occurred: ${error.message}`);
            }
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper>
                    <Box p={5}>
                        <Grid container>
                            <Grid item xs={12} container>
                                <Box>
                                    <Typography variant="h6">
                                        {readonly ? 'View Traffic Mirror Filter'
                                            : (isUpdate
                                                ? 'Update Traffic Mirror Filter'
                                                : 'Create Traffic Mirror Filter')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} container spacing={2} marginTop={2}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal">
                                        <TextField
                                            label="Filter Description"
                                            name="Description"
                                            value={filterdata.Description}
                                            onChange={handleChange}
                                            disabled={readonly || isUpdate}
                                        />
                                    </FormControl>
                                    <Box mt={4}>
                                        <Box mt={2}>
                                            <FormLabel>Network Service</FormLabel>
                                        </Box>
                                        <FormControlLabel
                                            label="amazon-dns"
                                            labelPlacement="start"
                                            control={
                                                <Checkbox
                                                    disabled={readonly}
                                                    checked={filterdata.dns || false}
                                                    onChange={handleChange}
                                                    name="dns"
                                                    color="primary"
                                                />
                                            }
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} mt={2} container justifyContent="flex-end">
                                <Box mr={2}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={classes.button}
                                        onClick={handleFilterClose}
                                    >{readonly ? 'Back' : 'Cancel'}</Button>
                                    {!readonly && <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        className={classes.button}
                                        onClick={handleSubmit}
                                        disabled={!hasChanges}
                                    >
                                        {isUpdate ? 'Update' : 'Save'}
                                    </Button>}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CreateFilter;
